import { useState, useEffect, useCallback } from 'react';
import { getInvestigadores, clearInvestigadores } from '../utils/storage';
import { downloadExcel } from '../utils/excel';
import { getEncuestas, interpretarSUS, EncuestaRecord } from '../utils/encuesta';
import { downloadExcelEncuesta } from '../utils/excelEncuesta';
import { InvestigadorRecord } from '../types';
import GraficasAdmin from './GraficasAdmin';
import RegistrosAdminTable from './RegistrosAdminTable';

export default function DescargaExcelSection() {
  const [records, setRecords] = useState<InvestigadorRecord[]>([]);
  const [encuestas, setEncuestas] = useState<EncuestaRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingEncuestas, setLoadingEncuestas] = useState(true);
  const [clearing, setClearing] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    setErrorMsg('');
    const data = await getInvestigadores();
    setRecords(data);
    setLoading(false);
  }, []);

  const fetchEncuestas = useCallback(async () => {
    setLoadingEncuestas(true);
    const data = await getEncuestas();
    setEncuestas(data);
    setLoadingEncuestas(false);
  }, []);

  useEffect(() => {
    fetchRecords();
    fetchEncuestas();
  }, [fetchRecords, fetchEncuestas]);

  const handleDownload = () => {
    if (records.length === 0) return;
    downloadExcel(records);
  };

  const handleDownloadEncuesta = () => {
    if (encuestas.length === 0) return;
    downloadExcelEncuesta(encuestas);
  };

  const promSUS = encuestas.length > 0
    ? Math.round(encuestas.reduce((acc, r) => acc + r.puntaje_sus, 0) / encuestas.length)
    : null;

  const handleClear = async () => {
    setClearing(true);
    const ok = await clearInvestigadores();
    if (ok) {
      setRecords([]);
    } else {
      setErrorMsg('No se pudieron eliminar los registros. Intente nuevamente.');
    }
    setClearing(false);
    setShowConfirm(false);
  };

  return (
    <section id="descarga-excel" className="max-w-5xl mx-auto px-6 mt-6 mb-12">
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="bg-slate-50 border-b border-slate-200 px-6 py-4 flex items-center gap-3">
          <div className="w-8 h-8 flex items-center justify-center bg-emerald-600 rounded-lg">
            <i className="ri-file-excel-2-line text-white text-sm" />
          </div>
          <div className="flex-1">
            <h2 className="text-[#0D3157] font-bold text-base">Descarga de Datos — Excel</h2>
            <p className="text-slate-400 text-xs">Exportar registros del censo en formato .xlsx</p>
          </div>
          <div className="flex items-center gap-2 text-xs text-slate-400">
            <i className="ri-database-2-line text-emerald-500" />
            <span className="text-emerald-600 font-medium">Supabase</span>
          </div>
        </div>

        <div className="p-6">
          {errorMsg && (
            <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-4">
              <i className="ri-error-warning-line text-red-500 text-sm shrink-0" />
              <p className="text-red-600 text-sm">{errorMsg}</p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-[#0D3157]/5 rounded-lg p-4 text-center">
              {loading ? (
                <div className="flex items-center justify-center h-10">
                  <i className="ri-loader-4-line animate-spin text-[#0D3157] text-2xl" />
                </div>
              ) : (
                <p className="text-3xl font-bold text-[#0D3157]">{records.length}</p>
              )}
              <p className="text-xs text-slate-500 font-medium mt-1">Registros totales</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-4 text-center">
              {loading ? (
                <div className="flex items-center justify-center h-10">
                  <i className="ri-loader-4-line animate-spin text-slate-400 text-2xl" />
                </div>
              ) : (
                <p className="text-3xl font-bold text-slate-600">
                  {records.filter((r) => r.tipoUsuario === 'docente').length}
                </p>
              )}
              <p className="text-xs text-slate-500 font-medium mt-1">Docentes registrados</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-4 text-center">
              {loading ? (
                <div className="flex items-center justify-center h-10">
                  <i className="ri-loader-4-line animate-spin text-slate-400 text-2xl" />
                </div>
              ) : (
                <p className="text-3xl font-bold text-slate-600">
                  {records.filter((r) => r.tipoUsuario === 'estudiante').length}
                </p>
              )}
              <p className="text-xs text-slate-500 font-medium mt-1">Estudiantes registrados</p>
            </div>
          </div>

          {loading ? (
            <div className="flex flex-col items-center py-12 text-slate-400">
              <i className="ri-loader-4-line animate-spin text-3xl mb-3 text-[#0D3157]" />
              <p className="text-sm font-medium text-slate-500">Cargando registros desde Supabase…</p>
            </div>
          ) : records.length > 0 ? (
            <>
              <RegistrosAdminTable records={records} />

              <div className="flex items-center gap-3 flex-wrap mt-4">
                <button
                  type="button"
                  onClick={handleDownload}
                  className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-sm px-5 py-2.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-download-2-line" />
                  Descargar Excel (.xlsx)
                </button>
                <button
                  type="button"
                  onClick={fetchRecords}
                  disabled={loading}
                  className="flex items-center gap-2 bg-slate-100 hover:bg-slate-200 text-slate-600 font-semibold text-sm px-4 py-2.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                >
                  <i className={`ri-refresh-line ${loading ? 'animate-spin' : ''}`} />
                  Actualizar lista
                </button>
                <button
                  type="button"
                  onClick={() => setShowConfirm(true)}
                  className="flex items-center gap-2 text-red-500 hover:text-red-700 font-semibold text-sm px-4 py-2.5 rounded-lg hover:bg-red-50 transition-colors cursor-pointer whitespace-nowrap ml-auto"
                >
                  <i className="ri-delete-bin-2-line" />
                  Limpiar registros
                </button>
              </div>

              <GraficasAdmin records={records} />
            </>
          ) : (
            <div className="text-center py-10 text-slate-400">
              <div className="w-16 h-16 flex items-center justify-center bg-slate-100 rounded-full mx-auto mb-3">
                <i className="ri-file-excel-2-line text-3xl text-slate-300" />
              </div>
              <p className="text-sm font-semibold text-slate-500">
                No hay registros almacenados aún
              </p>
              <p className="text-xs mt-1 max-w-sm mx-auto leading-relaxed">
                Los registros aparecerán aquí después de que los investigadores
                completen y envíen el formulario.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* ── Sección Encuesta de Satisfacción ─────────────────── */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden mt-5">
        <div className="bg-slate-50 border-b border-slate-200 px-6 py-4 flex items-center gap-3">
          <div className="w-8 h-8 flex items-center justify-center bg-violet-600 rounded-lg">
            <i className="ri-survey-line text-white text-sm" />
          </div>
          <div className="flex-1">
            <h2 className="text-[#0D3157] font-bold text-base">Resultados de Encuesta SUS</h2>
            <p className="text-slate-400 text-xs">System Usability Scale — Estándar internacional de usabilidad</p>
          </div>
          {!loadingEncuestas && encuestas.length > 0 && promSUS !== null && (
            <div className="flex items-center gap-2">
              <span className={`text-xs font-bold px-3 py-1 rounded-full ${
                promSUS >= 85 ? 'bg-emerald-100 text-emerald-700' :
                promSUS >= 71 ? 'bg-green-100 text-green-700' :
                promSUS >= 51 ? 'bg-amber-100 text-amber-700' :
                'bg-red-100 text-red-700'
              }`}>
                SUS: {promSUS}/100 — {interpretarSUS(promSUS)}
              </span>
            </div>
          )}
        </div>

        <div className="p-6">
          {loadingEncuestas ? (
            <div className="flex flex-col items-center py-10 text-slate-400">
              <i className="ri-loader-4-line animate-spin text-3xl mb-3 text-violet-500" />
              <p className="text-sm font-medium text-slate-500">Cargando encuestas…</p>
            </div>
          ) : encuestas.length > 0 ? (
            <>
              {/* Estadísticas rápidas */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
                <div className="bg-violet-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-violet-700">{encuestas.length}</p>
                  <p className="text-xs text-slate-500 mt-0.5">Encuestas recibidas</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-[#0D3157]">{promSUS}</p>
                  <p className="text-xs text-slate-500 mt-0.5">Puntaje SUS promedio</p>
                </div>
                <div className="bg-emerald-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-emerald-700">
                    {encuestas.filter((e) => e.puntaje_sus >= 71).length}
                  </p>
                  <p className="text-xs text-slate-500 mt-0.5">Valoraciones positivas</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-slate-600">
                    {encuestas.filter((e) => e.comentario && e.comentario.trim()).length}
                  </p>
                  <p className="text-xs text-slate-500 mt-0.5">Con comentarios</p>
                </div>
              </div>

              {/* Últimos comentarios */}
              {encuestas.filter((e) => e.comentario?.trim()).length > 0 && (
                <div className="mb-5">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
                    Últimos comentarios
                  </p>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {encuestas
                      .filter((e) => e.comentario?.trim())
                      .slice(0, 5)
                      .map((e, idx) => (
                        <div key={idx} className="bg-slate-50 rounded-lg px-4 py-2.5 flex items-start gap-3">
                          <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <i className="ri-chat-quote-line text-slate-400 text-sm" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs text-slate-600 leading-relaxed">{e.comentario}</p>
                            <p className="text-xs text-slate-400 mt-1">
                              SUS: <strong className="text-[#0D3157]">{e.puntaje_sus}</strong> — {new Date(e.fecha_registro).toLocaleDateString('es-BO')}
                            </p>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              )}

              <div className="flex items-center gap-3 flex-wrap">
                <button
                  type="button"
                  onClick={handleDownloadEncuesta}
                  className="flex items-center gap-2 bg-violet-600 hover:bg-violet-700 text-white font-semibold text-sm px-5 py-2.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-bar-chart-2-line" />
                  Descargar resultados (.xlsx)
                </button>
                <button
                  type="button"
                  onClick={fetchEncuestas}
                  disabled={loadingEncuestas}
                  className="flex items-center gap-2 bg-slate-100 hover:bg-slate-200 text-slate-600 font-semibold text-sm px-4 py-2.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                >
                  <i className={`ri-refresh-line ${loadingEncuestas ? 'animate-spin' : ''}`} />
                  Actualizar
                </button>
              </div>
            </>
          ) : (
            <div className="text-center py-10 text-slate-400">
              <div className="w-16 h-16 flex items-center justify-center bg-violet-50 rounded-full mx-auto mb-3">
                <i className="ri-survey-line text-3xl text-violet-300" />
              </div>
              <p className="text-sm font-semibold text-slate-500">
                Aún no hay encuestas respondidas
              </p>
              <p className="text-xs mt-1 max-w-sm mx-auto leading-relaxed">
                Las encuestas aparecerán aquí cuando los usuarios completen el formulario de registro.
              </p>
            </div>
          )}
        </div>
      </div>

      {showConfirm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-sm w-full mx-4">
            <div className="w-12 h-12 flex items-center justify-center bg-red-100 rounded-full mx-auto mb-4">
              <i className="ri-alert-line text-red-500 text-xl" />
            </div>
            <h3 className="text-center font-bold text-slate-700 mb-2">
              ¿Eliminar todos los registros?
            </h3>
            <p className="text-center text-slate-500 text-sm mb-5">
              Esta acción no se puede deshacer. Asegúrese de descargar el Excel
              antes de limpiar los datos.
            </p>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setShowConfirm(false)}
                disabled={clearing}
                className="flex-1 border border-slate-300 text-slate-600 font-semibold text-sm py-2.5 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleClear}
                disabled={clearing}
                className="flex-1 bg-red-500 hover:bg-red-600 text-white font-semibold text-sm py-2.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
              >
                {clearing ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin" />
                    Eliminando…
                  </span>
                ) : (
                  'Sí, eliminar'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}